import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "../components/UserMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, FileText, Plus, Eye, Pencil, Download, Trash2, ClipboardCheck, BarChart3, AlertTriangle,
  PackageSearch, Unlink, CheckCircle2, RefreshCw,
} from "lucide-react";
import { useStickyState } from "../safety/listPrefs";
import ResetFiltersChip from "../safety/ResetFiltersChip";
import SafetyShell from "../safety/SafetyShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const STATUS_BADGES = {
  draft: { label: "Draft", className: "bg-gray-100 text-gray-700" },
  pending_approval: { label: "Pending Approval", className: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved", className: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", className: "bg-red-100 text-red-700" },
};

export default function ProductionReports({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [statusFilter, setStatusFilter] = useStickyState("safety:reports:statusFilter", "all");
  const [fromDate, setFromDate] = useStickyState("safety:reports:fromDate", "");
  const [toDate, setToDate] = useStickyState("safety:reports:toDate", "");

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, fromDate, toDate]);

  const load = async () => {
    try {
      const permRes = await axios.get(`${API}/auth/me/permissions`, auth());
      setPermissions(permRes.data.effective_permissions || []);
      let url = `${API}/production/reports?`;
      if (statusFilter !== "all") url += `status_filter=${statusFilter}&`;
      if (fromDate) url += `from_date=${fromDate}&`;
      if (toDate) url += `to_date=${toDate}&`;
      const res = await axios.get(url, auth());
      setReports(res.data);
      if ((permRes.data.effective_permissions || []).includes("production.create")) {
        loadOrphanCount();
      }
    } catch (e) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const hasPerm = (p) => permissions.includes(p);
  const canCreate = hasPerm("production.create");
  const canApprove = hasPerm("production.approve");
  const canDeleteProtected = hasPerm("production.delete_protected");

  const downloadPdf = async (id, date) => {
    try {
      const res = await axios.get(`${API}/production/reports/${id}/pdf`, { ...auth(), responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `DailyProductionReport_${date}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to download PDF"); }
  };

  const [deleteDialog, setDeleteDialog] = useState({ open: false, report: null, confirmText: "" });
  const openDeleteDialog = (report) => setDeleteDialog({ open: true, report, confirmText: "" });
  const closeDeleteDialog = () => setDeleteDialog({ open: false, report: null, confirmText: "" });
  const isProtectedStatus = (s) => s === "approved" || s === "pending_approval";

  const confirmDelete = async () => {
    const rep = deleteDialog.report;
    if (!rep) return;
    try {
      await axios.delete(`${API}/production/reports/${rep.id}`, auth());
      toast.success("Report deleted");
      closeDeleteDialog();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  // ── Pallet ↔ Report reconciliation ──────────────────────────────────────
  const [reconOpen, setReconOpen] = useState(false);
  const [reconLoading, setReconLoading] = useState(false);
  const [recon, setRecon] = useState(null);
  const [reconTab, setReconTab] = useState("orphaned");
  const [releasing, setReleasing] = useState(false);
  const [orphanCount, setOrphanCount] = useState(0);

  const loadOrphanCount = async () => {
    try {
      const res = await axios.get(`${API}/production-reports/pallet-reconciliation`, auth());
      setOrphanCount(res.data?.summary?.orphaned || 0);
    } catch (e) { /* non-blocking */ }
  };

  const loadRecon = async () => {
    setReconLoading(true);
    try {
      const res = await axios.get(`${API}/production-reports/pallet-reconciliation`, auth());
      setRecon(res.data);
      setOrphanCount(res.data?.summary?.orphaned || 0);
      // Default to the tab that most needs attention.
      setReconTab((res.data?.summary?.orphaned || 0) > 0 ? "orphaned" : "available");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reconciliation");
    } finally {
      setReconLoading(false);
    }
  };

  const openRecon = () => { setReconOpen(true); loadRecon(); };

  const releaseAllOrphans = async () => {
    const ids = (recon?.orphaned || []).map((p) => p.id);
    if (!ids.length) return;
    setReleasing(true);
    try {
      const res = await axios.post(`${API}/production-reports/release-pallets`, { pallet_ids: ids }, auth());
      toast.success(`${res.data.count} pallet(s) released back to the picker`);
      await loadRecon();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to release pallets");
    } finally {
      setReleasing(false);
    }
  };

  const releaseOne = async (id) => {
    setReleasing(true);
    try {
      const res = await axios.post(`${API}/production-reports/release-pallets`, { pallet_ids: [id] }, auth());
      toast.success(`${res.data.count} pallet released`);
      await loadRecon();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to release pallet");
    } finally {
      setReleasing(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
    </div>;
  }

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Production Reports" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Production Reports", active: true, depth: 1 },
        { label: "Pending Approvals", to: "/safety/production-reports/approvals", depth: 2 },
        { label: "Pending Tracker", to: "/safety/pending-production", depth: 2 },
        { label: "Purchase Orders", to: "/safety/po", depth: 1 },
      ]}
    >
      <div data-testid="production-reports-page">
      <Toaster position="top-right" richColors />
      {/* Page header */}
      <div className="sf-surface p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-orange-600" />
          <div>
            <h1 className="text-xl font-bold tracking-tight">Daily Production Reports</h1>
            <p className="text-xs text-zinc-500">Powder Coating · MEPL/QMS/PFC/05</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => navigate("/safety/pending-production")} variant="outline" size="sm" data-testid="pending-production-nav-btn">
            <BarChart3 className="w-4 h-4 mr-1.5" /> Pending Production
          </Button>
          {canCreate && (
            <Button onClick={openRecon} variant="outline" size="sm" className="relative" data-testid="pallet-recon-nav-btn">
              <PackageSearch className="w-4 h-4 mr-1.5" /> Pallet Reconciliation
              {orphanCount > 0 && (
                <span
                  className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold"
                  data-testid="pallet-recon-orphan-badge"
                  title={`${orphanCount} orphaned pallet(s) hidden from the DPR picker`}
                >
                  {orphanCount}
                </span>
              )}
            </Button>
          )}
          {canApprove && (
            <Button onClick={() => navigate("/safety/production-reports/approvals")} variant="outline" size="sm" data-testid="approvals-nav-btn">
              <ClipboardCheck className="w-4 h-4 mr-1.5" /> Pending Approvals
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" data-testid="from-date" />
            <span className="text-gray-400">to</span>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" data-testid="to-date" />
            <ResetFiltersChip
              active={statusFilter !== "all" || fromDate !== "" || toDate !== ""}
              onReset={() => { setStatusFilter("all"); setFromDate(""); setToDate(""); }}
            />
          </div>
          {canCreate && (
            <Button onClick={() => navigate("/safety/production-reports/new")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-report-btn">
              <Plus className="w-4 h-4 mr-1" /> New Report
            </Button>
          )}
        </div>

        <div className="bg-white rounded-lg border shadow-sm">
          <table className="w-full text-sm">            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">SO Nos</th>
                <th className="p-3 text-left">Customer</th>
                <th className="p-3 text-right">Sq Ft</th>
                <th className="p-3 text-left">Created By</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-gray-500" data-testid="no-reports">No reports yet.</td></tr>}
              {reports.map(r => {
                const badge = STATUS_BADGES[r.status] || STATUS_BADGES.draft;
                const isCreator = r.created_by === user?.email;
                const canEdit =
                  (r.status === "draft" && isCreator) ||
                  (r.status === "pending_approval" && (isCreator || canApprove));
                const canDelete =
                  ((r.status === "draft" || r.status === "rejected") && isCreator) ||
                  (isProtectedStatus(r.status) && canDeleteProtected);
                return (
                  <tr key={r.id} className="border-b hover:bg-gray-50" data-testid={`report-row-${r.id}`}>
                    <td className="p-3 font-medium">
                      {r.report_date}
                      {r.edited_after_submit && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 align-middle" title="Edited after submission" data-testid={`edited-marker-${r.id}`}>
                          edited
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-gray-600">{(r.po_numbers || []).join(", ") || "-"}</td>
                    <td className="p-3 text-gray-600">{(r.customer_names || []).join(", ") || "-"}</td>
                    <td className="p-3 text-right">{r.total_sq_ft ?? "-"}</td>
                    <td className="p-3 text-gray-600">{r.created_by_name || r.created_by}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs rounded ${badge.className}`}>{badge.label}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/safety/production-reports/${r.id}`)} data-testid={`view-report-${r.id}`}>
                          <Eye className="w-3 h-3" />
                        </Button>
                        {canEdit && (
                          <Button size="sm" variant="outline" onClick={() => navigate(`/safety/production-reports/${r.id}/edit`)} data-testid={`edit-report-${r.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => downloadPdf(r.id, r.report_date)} data-testid={`pdf-report-${r.id}`}>
                          <Download className="w-3 h-3" />
                        </Button>
                        {canDelete && (
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => openDeleteDialog(r)} data-testid={`delete-report-${r.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pallet ↔ Report reconciliation */}
      <Dialog open={reconOpen} onOpenChange={setReconOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="pallet-recon-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageSearch className="w-5 h-5 text-orange-600" /> Pallet ↔ Report Reconciliation
            </DialogTitle>
            <DialogDescription>
              Every produced pallet is either <b>available</b> (shows in the DPR picker),
              <b> attached</b> to a report, or <b>orphaned</b> (hidden from the picker but not on any
              live report). Release orphans to bring them back to the picker.
            </DialogDescription>
          </DialogHeader>

          {reconLoading ? (
            <div className="py-12 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            </div>
          ) : recon ? (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Summary tabs */}
              <div className="flex gap-2 mb-3 flex-wrap">
                {[
                  { key: "available", label: "Available", count: recon.summary.available,
                    on: "bg-emerald-600 text-white border-emerald-600",
                    off: "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50" },
                  { key: "attached", label: "Attached", count: recon.summary.attached,
                    on: "bg-blue-600 text-white border-blue-600",
                    off: "bg-white text-blue-700 border-blue-200 hover:bg-blue-50" },
                  { key: "orphaned", label: "Orphaned", count: recon.summary.orphaned,
                    on: "bg-red-600 text-white border-red-600",
                    off: "bg-white text-red-700 border-red-200 hover:bg-red-50" },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setReconTab(t.key)}
                    data-testid={`recon-tab-${t.key}`}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                      reconTab === t.key ? t.on : t.off
                    }`}
                  >
                    {t.label} <span className="ml-1 font-bold">{t.count}</span>
                  </button>
                ))}
                <div className="flex-1" />
                <Button size="sm" variant="outline" onClick={loadRecon} data-testid="recon-refresh-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                </Button>
              </div>

              {reconTab === "orphaned" && recon.summary.orphaned > 0 && (
                <div className="mb-3 flex items-center justify-between gap-2 bg-red-50 border border-red-200 rounded-md p-2.5">
                  <p className="text-xs text-red-700">
                    <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                    {recon.summary.orphaned} pallet(s) are stamped to a report that was deleted or no longer lists them.
                  </p>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" disabled={releasing}
                    onClick={releaseAllOrphans} data-testid="recon-release-all-btn">
                    <Unlink className="w-3.5 h-3.5 mr-1" /> Release all
                  </Button>
                </div>
              )}

              <div className="flex-1 overflow-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b sticky top-0">
                    <tr>
                      <th className="p-2 text-left">Pallet ID</th>
                      <th className="p-2 text-left">Product</th>
                      <th className="p-2 text-left">SO</th>
                      <th className="p-2 text-right">Qty</th>
                      {reconTab === "available" && <th className="p-2 text-left">Operator</th>}
                      {reconTab === "attached" && <th className="p-2 text-left">Report</th>}
                      {reconTab === "orphaned" && <th className="p-2 text-left">Reason</th>}
                      {reconTab === "orphaned" && <th className="p-2 text-left">Action</th>}
                    </tr>
                  </thead>
                  <tbody data-testid={`recon-table-${reconTab}`}>
                    {(recon[reconTab] || []).length === 0 && (
                      <tr><td colSpan="7" className="p-6 text-center text-gray-500" data-testid="recon-empty">
                        {reconTab === "orphaned" ? "No orphaned pallets — everything reconciles cleanly. ✅" : "None."}
                      </td></tr>
                    )}
                    {(recon[reconTab] || []).map((p) => (
                      <tr key={p.id} className="border-b hover:bg-gray-50" data-testid={`recon-row-${p.id}`}>
                        <td className="p-2 font-mono text-xs">{p.id}</td>
                        <td className="p-2">{p.product_code || "-"}</td>
                        <td className="p-2 text-gray-600">{p.po_no || "-"}</td>
                        <td className="p-2 text-right">{p.qty}</td>
                        {reconTab === "available" && <td className="p-2 text-gray-600">{p.paired_by_email || "-"}</td>}
                        {reconTab === "attached" && (
                          <td className="p-2 text-gray-600">
                            {p.report_date || "-"}{" "}
                            <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${(STATUS_BADGES[p.report_status] || STATUS_BADGES.draft).className}`}>
                              {(STATUS_BADGES[p.report_status] || STATUS_BADGES.draft).label}
                            </span>
                          </td>
                        )}
                        {reconTab === "orphaned" && (
                          <td className="p-2 text-red-600 text-xs">
                            {p.orphan_reason === "report_deleted" ? "Report deleted" : "Not listed in report"}
                          </td>
                        )}
                        {reconTab === "orphaned" && (
                          <td className="p-2">
                            <Button size="sm" variant="outline" className="text-red-600 h-7" disabled={releasing}
                              onClick={() => releaseOne(p.id)} data-testid={`recon-release-${p.id}`}>
                              <Unlink className="w-3 h-3 mr-1" /> Release
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReconOpen(false)} data-testid="recon-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Type-to-confirm Delete Dialog (approved / pending-approval reports require protected permission) */}
      <Dialog open={deleteDialog.open} onOpenChange={(v) => !v && closeDeleteDialog()}>
        <DialogContent data-testid="delete-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete {deleteDialog.report?.status === "approved" ? "Approved"
                : deleteDialog.report?.status === "pending_approval" ? "Pending-Approval"
                : "Draft"} Report?
            </DialogTitle>
            <DialogDescription>
              This action is permanent. The report dated
              {" "}<span className="font-semibold">{deleteDialog.report?.report_date}</span>
              {" "}will be removed{deleteDialog.report?.status === "approved" ? " and linked PO statuses will be recomputed." : "."}
            </DialogDescription>
          </DialogHeader>
          {isProtectedStatus(deleteDialog.report?.status) ? (
            <div className="space-y-2">
              <Label className="text-sm">
                To confirm, type the report date{" "}
                <code className="bg-gray-100 px-1 rounded text-xs">{deleteDialog.report?.report_date}</code>
              </Label>
              <Input
                value={deleteDialog.confirmText}
                onChange={(e) => setDeleteDialog({ ...deleteDialog, confirmText: e.target.value })}
                placeholder={deleteDialog.report?.report_date || ""}
                data-testid="delete-confirm-input"
                autoFocus
              />
            </div>
          ) : (
            <p className="text-sm text-gray-600">This is a draft/rejected report. Click Delete to confirm.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDeleteDialog} data-testid="cancel-delete-btn">Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={
                isProtectedStatus(deleteDialog.report?.status) &&
                deleteDialog.confirmText !== (deleteDialog.report?.report_date || "")
              }
              onClick={confirmDelete}
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </SafetyShell>
  );
}
